import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, CheckCircle2, Star, EyeOff } from "lucide-react";
import { fmt, PAGE_SIZE } from "@/pages/admin/utils/format";
import { PaginationFooter } from "@/pages/admin/components/common";



// ---------------------------------------------------------------------------
// Iteration 29 — Admin Reviews moderation (paginated, filterable by status)
// ---------------------------------------------------------------------------
const STAR_LABEL = (n) => `${n} من 5`;


const AdminStars = ({ value }) => (
  <div className="inline-flex gap-0.5" aria-label={STAR_LABEL(value)}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-3.5 h-3.5 ${n <= value ? "fill-[#C5A059] text-[#C5A059]" : "text-[#D9D5CC]"}`}
      />
    ))}
  </div>
);


const REVIEW_STATUS_LABELS = {
  pending: "بانتظار المراجعة",
  approved: "منشور",
  hidden: "مخفي",
};


const REVIEW_STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  hidden: "bg-stone-200 text-stone-700",
};


const AdminReviews = () => {
  const [filter, setFilter] = useState("pending"); // pending | approved | hidden | all
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);

  const fetchPage = async () => {
    const params = { page, limit: PAGE_SIZE };
    if (filter !== "all") params.status = filter;
    try {
      const r = await api.get("/admin/reviews", { params });
      setItems(r.data.items || []);
      setMeta({ total: r.data.total ?? 0, total_pages: r.data.total_pages ?? 1 });
    } catch {
      // soft-fail
    }
  };

  useEffect(() => { setPage(1); }, [filter]);
  useEffect(() => { fetchPage(); /* eslint-disable-next-line */ }, [filter, page]);

  const moderate = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      toast.success(status === "approved" ? "تم النشر" : status === "hidden" ? "تم الإخفاء" : "تم التحديث");
      fetchPage();
    } catch {
      toast.error("تعذّر التحديث");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("سيتم حذف التقييم نهائيًا. هل أنت متأكد؟")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("تم الحذف");
      fetchPage();
    } catch {
      toast.error("تعذّر الحذف");
    }
  };

  const FILTERS = [
    { key: "pending", label: "بانتظار المراجعة" },
    { key: "approved", label: "المنشورة" },
    { key: "hidden", label: "المخفية" },
    { key: "all", label: "الكل" },
  ];

  return (
    <div data-testid="admin-reviews">
      <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-reviews-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            data-testid={`admin-reviews-filter-${f.key}`}
            className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === f.key
                ? "bg-[#0A4D3C] text-white border-[#0A4D3C]"
                : "bg-white text-[#5E5C58] border-[#E5E1D8] hover:bg-[#F6F4EE]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="card-warm p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-reviews-table">
          <thead className="bg-[#F6F4EE] text-[#5E5C58]">
            <tr>
              <th className="text-start p-4">العميل</th>
              <th className="text-start p-4">التقييم</th>
              <th className="text-start p-4">التعليق</th>
              <th className="text-start p-4">المصدر</th>
              <th className="text-start p-4">التاريخ</th>
              <th className="text-start p-4">الحالة</th>
              <th className="text-start p-4">إجراء</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-[#5E5C58]">لا توجد تقييمات.</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-[#E5E1D8] align-top" data-testid={`admin-review-row-${r.id}`}>
                <td className="p-4">
                  <div className="font-medium">{r.user_name || "—"}</div>
                  <div className="text-xs text-[#5E5C58]" dir="ltr">{r.user_email}</div>
                </td>
                <td className="p-4"><AdminStars value={r.rating} /></td>
                <td className="p-4 max-w-md whitespace-pre-wrap text-[#222222]">
                  {r.comment ? r.comment : <span className="text-[#8B8780]">— بدون تعليق</span>}
                </td>
                <td className="p-4 text-xs text-[#5E5C58]">
                  {r.kind === "booking" ? "جلسة" : "كتاب"}
                </td>
                <td className="p-4 text-xs text-[#5E5C58]">{fmt(r.created_at)}</td>
                <td className="p-4">
                  <Badge className={`rounded-full ${REVIEW_STATUS_BADGE[r.status] || ""}`}>
                    {REVIEW_STATUS_LABELS[r.status] || r.status}
                  </Badge>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {r.status !== "approved" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        data-testid={`admin-review-approve-${r.id}`}
                        onClick={() => moderate(r.id, "approved")}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 ms-1" /> نشر
                      </Button>
                    )}
                    {r.status !== "hidden" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        data-testid={`admin-review-hide-${r.id}`}
                        onClick={() => moderate(r.id, "hidden")}
                      >
                        <EyeOff className="w-3.5 h-3.5 ms-1" /> إخفاء
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full text-rose-700 hover:bg-rose-50"
                      data-testid={`admin-review-delete-${r.id}`}
                      onClick={() => remove(r.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5 ms-1" /> حذف
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationFooter
        page={page}
        totalPages={meta.total_pages}
        total={meta.total}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(meta.total_pages || 1, p + 1))}
        testId="admin-reviews-pager"
      />
    </div>
  );
};

export { AdminReviews };
